// Hazard Perception Animations
// 5 dashcam / first-person animations covering the most common CBR hazard scenarios
// View type: first-person (driver's-eye / dashcam perspective)

export interface HazardPerceptionAnimation {
  id: string;
  topic: string;
  sceneDescription: string;
  klingPrompt: string;
  negativePrompt: string;
  videoUrl: string | null;
  durationSeconds: number;
  viewType: 'top-down' | 'first-person' | 'third-person';
  freezeDescription: string;
  questionIds: string[];
}

export const hazardPerceptionAnimations: HazardPerceptionAnimation[] = [
  {
    id: 'hp-anim-1',
    topic: 'Hazard Perception',
    sceneDescription: 'Child hazard — urban street with parked cars',
    klingPrompt:
      'Dashcam footage from inside a car driving at 30-50 km/h along a quiet Dutch residential street lined with parked cars. ' +
      'A child on a small scooter suddenly rolls out from between two parked cars on the right side, entering the road. ' +
      'The child is not looking at traffic. The driver brakes firmly. Clean Dutch suburban setting, daytime, clear weather. ' +
      'Realistic, simple, no text or overlays.',
    negativePrompt:
      'No text, no captions, no animated characters, no cartoon style, no aerial view, no night time, no rain.',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/hp-anim-1.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription:
      'Child on scooter appearing from between parked cars — driver about to brake.',
    questionIds: ['q-hp-1', 'q-hp-6'],
  },
  {
    id: 'hp-anim-2',
    topic: 'Hazard Perception',
    sceneDescription: 'Pedestrian hazard — distracted pedestrian and zebra crossing',
    klingPrompt:
      'Dashcam footage from inside a car driving at 30 km/h along a Dutch residential street. ' +
      'A pedestrian is standing at a zebra crossing and steps onto the crossing as the car approaches 15 meters away. ' +
      'The driver brakes smoothly to a stop at the crossing. Daytime, dry road, clear zebra markings visible. ' +
      'Realistic, simple, no text or overlays.',
    negativePrompt:
      'No text, no captions, no aerial view, no cartoon style, no heavy traffic, no night scene.',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/hp-anim-2.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription:
      'Pedestrian stepping onto zebra crossing — car braking to give way.',
    questionIds: ['q-hp-2', 'q-hp-13'],
  },
  {
    id: 'hp-anim-3',
    topic: 'Hazard Perception',
    sceneDescription: 'Junction — car emerging from give-way line / opposing vehicle turning',
    klingPrompt:
      'Dashcam footage from inside a car driving at 50 km/h on a Dutch suburban road approaching a T-junction on the right. ' +
      'A car is partially hidden behind a hedge at the give-way line. Its front wheels begin to inch forward as if about to pull out. ' +
      'The driver releases the accelerator and slows, watching the junction carefully. ' +
      'Daytime, dry conditions, clear Dutch road markings. Realistic, simple, no text or overlays.',
    negativePrompt:
      'No text, no captions, no cartoon style, no aerial view, no motorway, no night scene.',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/hp-anim-3.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription:
      'Car at give-way line starting to creep forward — driver easing off accelerator.',
    questionIds: ['q-hp-4', 'q-hp-21'],
  },
  {
    id: 'hp-anim-4',
    topic: 'Hazard Perception',
    sceneDescription: 'Cyclist hazard — wobbling cyclist and opening car door',
    klingPrompt:
      'Dashcam footage from inside a car driving at 50 km/h on a wet Dutch road. ' +
      'A cyclist in the cycle lane ahead wobbles suddenly and moves slightly into the car lane. ' +
      'The driver eases off the accelerator and moves slightly to the right to give space. ' +
      'Wet road surface, daytime, realistic Dutch urban road, cycle lane clearly marked. ' +
      'Realistic, simple, no text or overlays.',
    negativePrompt:
      'No text, no captions, no cartoon style, no aerial view, no night scene, no heavy rain.',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/hp-anim-4.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription:
      'Cyclist wobbling into the car lane on a wet road — driver easing off gas and giving space.',
    questionIds: ['q-hp-11', 'q-hp-20', 'q-hp-22'],
  },
  {
    id: 'hp-anim-5',
    topic: 'Hazard Perception',
    sceneDescription: 'Motorway sudden hazard — car swerves / debris braking',
    klingPrompt:
      'Dashcam footage from inside a car driving at 100 km/h in the middle lane of a Dutch three-lane motorway. ' +
      'The car directly ahead suddenly swerves into the same lane without indicating, cutting across to avoid a slow vehicle. ' +
      'The driver reacts immediately by braking firmly. ' +
      'Daytime, clear motorway, Dutch road markings, realistic, no text or overlays.',
    negativePrompt:
      'No text, no captions, no cartoon style, no aerial view, no night, no crash or accident shown.',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/animations/hp-anim-5.mp4',
    durationSeconds: 6,
    viewType: 'first-person',
    freezeDescription:
      'Car swerving unexpectedly into lane at motorway speed — driver braking immediately.',
    questionIds: ['q-hp-5', 'q-hp-14'],
  },
];
